import type { ReactNode } from "react";
import { Space, Tag, Typography } from "antd";

export interface PageHeaderStat {
  label: ReactNode;
  /** Optional accent — `gold` for 会员/精选, `red` for the brand primary. */
  tone?: "default" | "red" | "gold";
}

export interface PageHeaderProps {
  /** Page title, e.g. 「品牌管理」. */
  title: ReactNode;
  /** One-line description shown under the title. */
  description?: ReactNode;
  /** Right-aligned primary action(s) — typically a 新建 button. */
  extra?: ReactNode;
  /** Small chips rendered next to the title, e.g.「共 128 条」. */
  stats?: PageHeaderStat[];
}

/**
 * Unified page header for admin list / detail pages.
 *
 * Layout: title (+ optional stat chips) and description on the left, primary
 * actions on the right. Provides the consistent "professional console" top
 * band used across every list page. Pure presentation — owns no data.
 */
export function PageHeader({ title, description, extra, stats }: PageHeaderProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 16,
        flexWrap: "wrap",
        marginBottom: 16,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <Space align="center" size={10} wrap>
          <Typography.Title level={4} style={{ margin: 0, lineHeight: 1.3 }}>
            {title}
          </Typography.Title>
          {stats?.map((s, i) => (
            <Tag
              key={i}
              bordered={false}
              color={s.tone === "red" ? "red" : s.tone === "gold" ? "gold" : "default"}
              style={{ fontWeight: 500, marginInlineEnd: 0 }}
            >
              {s.label}
            </Tag>
          ))}
        </Space>
        {description ? (
          <Typography.Paragraph
            type="secondary"
            style={{ margin: "6px 0 0", maxWidth: 720, fontSize: 13 }}
          >
            {description}
          </Typography.Paragraph>
        ) : null}
      </div>
      {extra ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {extra}
        </div>
      ) : null}
    </div>
  );
}

export default PageHeader;
